import { daRoleIdToKey, getRoleDaById } from "mocks/roleDA";
import moment from "moment";
import { FalconDataLists, FalconStandardData, FalconTableRow } from "types/Falcon";
import { FleetReportData } from "types/FleetReports";
import { PayrollFilters, PayrollRowData, PayrollUserData } from "types/Payroll";
import { ReportRecord } from "types/ShiftTracker";

export const getTimeSincePost = (date: string) => {
  let currentDate = moment().format();
  let expiredDate = moment(date).add(2, "days").format();
  let currentDateInJsFormat = new Date(currentDate);
  let expiredDateInJsFormat = new Date(expiredDate);
  let currentDateInMs = currentDateInJsFormat.getTime();
  let expiredDateInMs = expiredDateInJsFormat.getTime();

  return currentDateInMs > expiredDateInMs;
};

export const getTimeAfterPost = (date: string, days_ago: number) => {
  let currentDate = moment().format();
  let expiredDate = moment(date).subtract(days_ago, "days").format();
  let currentDateInJsFormat = new Date(currentDate);
  let expiredDateInJsFormat = new Date(expiredDate);
  let currentDateInMs = currentDateInJsFormat.getTime();
  let expiredDateInMs = expiredDateInJsFormat.getTime();

  return currentDateInMs > expiredDateInMs;
};

export const getFilteredShiftRecordIds = (
  rows: ReportRecord[],
  search: string,
  sortId?: string,
) => {
  const query = search?.trim().toLowerCase();
  const filtered = query
    ? rows.filter((row) => {
        const firstName = row.driver?.first_name?.toLowerCase() || "";
        const lastName = row.driver?.last_name?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`.trim();
        const roleName = String(daRoleIdToKey[row.role] || "").toLowerCase();
        const fleetGroup = (row.van?.van_group?.name || row.van?.van_group_name || "").toLowerCase();
        const vanNumber = String(row.van?.number ?? "").toLowerCase();

        return (
          fullName.includes(query) ||
          firstName.includes(query) ||
          lastName.includes(query) ||
          roleName.includes(query) ||
          fleetGroup.includes(query) ||
          vanNumber.includes(query)
        );
      })
    : rows;

  const columnId = sortId?.replace("-", "") || "id";
  const isDesc = !!sortId?.startsWith("-");

  return [...filtered]
    .sort((a, b) => compareShiftRecordSortValues(a, b, columnId, isDesc))
    .map((row) => row.id);
};

const getShiftRecordSortValue = (row: ReportRecord, columnId: string): string | number => {
  switch (columnId) {
    case "driver":
      return `${row.driver?.first_name || ""} ${row.driver?.last_name || ""}`.trim();
    case "badge":
      return row.driver?.badge || "";
    case "transporter_id":
      return row.driver?.transporter_id || "";
    case "second_driver":
      return `${row.second_driver?.first_name || ""} ${row.second_driver?.last_name || ""}`.trim();
    case "fleet_size":
      return row.van?.fleet_size?.id ?? "";
    case "van":
      return row.van?.number || "";
    case "company": {
      const group = row.van?.van_group;
      return (typeof group === "string" ? group : group?.name) || row.van?.van_group_name || "";
    }
    case "rabbit_number":
      return row.rabbit_number?.serial_number || "";
    case "dolly_number":
      return row.dolly_number?.serial_number || "";
    default: {
      const value = row[columnId as keyof ReportRecord];
      if (value == null || typeof value === "object") return "";
      return value as string | number | boolean as string | number;
    }
  }
};

const compareShiftRecordSortValues = (
  a: ReportRecord,
  b: ReportRecord,
  columnId: string,
  isDesc: boolean,
) => {
  const aValue = getShiftRecordSortValue(a, columnId);
  const bValue = getShiftRecordSortValue(b, columnId);

  if (typeof aValue === "number" && typeof bValue === "number") {
    return isDesc ? bValue - aValue : aValue - bValue;
  }

  const result = String(aValue).localeCompare(String(bValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return isDesc ? -result : result;
};

export const getFilteredFormData = ({
  searchValue,
  currentReport,
}: {
  searchValue: string;
  currentReport: ReportRecord[];
}) => {
  return currentReport.filter((obj) => {
    const values = [
      getRoleDaById(obj.role),
      obj?.rabbit_number?.serial_number,
      obj?.second_rabbit_number?.serial_number,
      obj?.dolly_number?.serial_number,
      obj?.second_dolly_number?.serial_number,
      obj?.van?.fleet_size?.title,
      obj?.driver?.first_name,
      obj?.driver?.last_name,
      obj?.van?.van_group_name,
      obj?.van?.number,
      obj?.van?.van_group?.name,
      obj?.driver?.first_name,
      obj?.driver?.last_name,
      obj?.driver?.transporter_id === "none"
        ? "nr"
        : obj?.driver?.transporter_id || "pending the id",
      obj?.van?.van_group?.name,
      obj?.van?.van_group_name,
      obj?.van?.number,
      obj?.route,
      `${obj?.uta}`,
      `${obj?.utl}`,
      `${obj?.nsl}`,
      `${obj?.bc}`,
      `${obj?.otp_failure}`,
      `${obj?.fdd}`,
      `${obj?.missort}`,
      `${obj?.oc}`,
      `${obj?.number_of_stops}`,
      `${obj?.number_of_packages}`,
      `${obj?.rescue_given}`,
      `${obj?.rescue_taken}`,
      `${obj?.missing}`,
      `${obj?.damage}`,
      `${obj?.rabbit}`,
      `${obj?.dolly_serial_number}`,
      `${obj?.rabbit_checklist}`,
      obj?.first_stop_time,
      obj?.planned_end_time,
      obj?.actual_end_time,
      obj?.actual_DA,
    ].filter((el) => el?.toLowerCase()?.includes(searchValue.toLowerCase())).length;
    return values > 0;
  });
};

export const filteredPayrollUserDataList = (
  userList: PayrollUserData[],
  payrollFilters: PayrollFilters,
  dataList?: PayrollRowData[],
) =>
  userList
    .filter((user: PayrollUserData) =>
      payrollFilters.user?.id ? user.id === payrollFilters.user?.id : true,
    )
    .filter((user: PayrollUserData) =>
      dataList ? dataList?.some((report: PayrollRowData) => report.user_id === user.id) : true,
    )
    .filter((user: PayrollUserData) =>
      Number(payrollFilters.station)
        ? user.station_spots.some(
            (spot: { station: number }) => spot.station === Number(payrollFilters.station),
          )
        : true,
    )
    .filter((user: PayrollUserData) =>
      payrollFilters.userRole ? user.role === payrollFilters.userRole : true,
    )
    .filter((user: PayrollUserData) =>
      payrollFilters.userStatus ? user.status === payrollFilters.userStatus : true,
    );

export const filteredPayrollDataList = (
  dataList: PayrollRowData[],
  payrollFilters: PayrollFilters,
) =>
  dataList
    .filter((report: PayrollRowData) =>
      payrollFilters.user?.id ? report.user_id === payrollFilters.user?.id : true,
    )
    .filter((report: PayrollRowData) =>
      Number(payrollFilters.station) ? report.station_id === Number(payrollFilters.station) : true,
    )
    .filter((report: PayrollRowData) =>
      payrollFilters.userRole ? report.user_role === payrollFilters.userRole : true,
    )
    .filter((report: PayrollRowData) =>
      payrollFilters.userStatus ? report.user_status === payrollFilters.userStatus : true,
    );

export const getFilteredVanData = (search: string, currentReports: FleetReportData[]) => {
  return currentReports.filter((obj) => {
    const values = [`${obj?.van_number}`, obj?.vin, obj?.plate].filter((el) =>
      el?.toLowerCase()?.includes(search?.toLowerCase()),
    ).length;
    return values > 0;
  });
};

export const getFilteredFalconData = (rowList: FalconTableRow[], dataList: FalconDataLists) => {
  return rowList?.filter((row: FalconTableRow) =>
    dataList?.standardList?.some(
      (data: FalconStandardData) =>
        data?.role === row?.role &&
        row?.hour?.includes(data?.role_hours) &&
        row?.vans?.includes(
          data?.van__fleet_size ? data?.van__fleet_size : data?.cancelled_fleet_size,
        ),
    ),
  );
};
